/**
 * AuthContext — single source of truth for the customer session in the
 * Customer-Portal UI. Mirrors the apps/admin auth-context shape with
 * three portal-specific extensions:
 *
 *  1. Two-step login (`/v1/portal/auth/login` → returns
 *     `{ status: 'totp_required', login_token }` when TOTP enrolled;
 *     `/v1/portal/auth/login/totp` consumes the login_token + code and
 *     sets the session cookie).
 *  2. Optional signup pre-state (`unauthenticated` covers it; the
 *     /portal/signup page just exercises the same login flow on success).
 *  3. Email-verification surfacing — `user.email_verified` is part of
 *     the /v1/portal/auth/me payload so the shell can render a banner
 *     until verification completes (the verification flow itself lives
 *     on /portal/verify-email and is handled outside this context).
 *
 * On mount, hits `GET /v1/portal/auth/me`. Resolved states:
 *   - `status: 'loading'`         — initial fetch in flight
 *   - `status: 'authenticated'`   — /v1/portal/auth/me returned 200
 *   - `status: 'unauthenticated'` — anything else (401, network error)
 *
 * Logout clears local state even if the server-side revoke fails — the
 * user wanted to log out, and the cookie either got cleared or will
 * expire on its own.
 */
import { applyLocale, currentLocale, isPortalLocale, type PortalLocale } from '../i18n';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { PortalUserRole, PortalUserPayload } from '@orboto/ocp-shared';

import { api, ApiError } from './api';
import { getPortalConfig, usePortalConfig } from './portal-config';
import { resetOmsSubscriptionCache } from './use-has-oms-subscription';
import { resetAffiliateCache } from './use-affiliate';

// Re-export so page components / tests can keep importing the type
// from this module — canonical source is @orboto/ocp-shared.
export type { PortalUserRole };

export interface PortalUser {
  id: string;
  customerId: string;
  email: string;
  emailVerified: boolean;
  role: PortalUserRole;
  totpEnabled: boolean;
  /**
   * OCP-515 - a redeem code the customer carried through signup but has not
   * claimed yet (null when none). Lets the shell auto-continue to
   * /redeem?code=X after a cross-device email-verify or a fresh-device login,
   * where the browser-local sessionStorage carry is empty.
   */
  pendingRedeemCode: string | null;
  /** OCP-656 - the dashboard language stored for this user (null = never chosen). */
  locale: PortalLocale | null;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

/** Two-step login: when TOTP is required, the server returns this so
 * the UI can render a code-prompt without re-asking for the password. */
export interface TotpChallenge {
  status: 'totp_required';
  loginToken: string;
}

export type LoginResult = { status: 'authenticated' } | TotpChallenge;

export interface AuthContextValue {
  status: AuthStatus;
  user: PortalUser | null;
  /**
   * Step 1 of login. Returns `'authenticated'` directly when the user
   * has no TOTP enrolled, or `'totp_required'` with a one-shot
   * `loginToken` to hand to `loginTotp`.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  /**
   * Step 2 of login (only when step 1 returned `totp_required`).
   * On success sets the session and resolves.
   */
  loginTotp: (loginToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Re-fetch `/v1/portal/auth/me` — used after the verify-email flow
   * completes so the banner disappears without a hard reload.
   */
  refresh: () => Promise<void>;
  /**
   * OCP-656 - switch the dashboard language: applies it locally at once and,
   * when signed in, stores it on the user (`PATCH /v1/portal/me/locale`).
   */
  setLocale: (locale: PortalLocale) => Promise<void>;
  /**
   * OCP-656 - whether the language switch is available (`/v1/portal/config`).
   * Always true since OCP-668 (launch); kept as the one source for the
   * toggle and the Settings section.
   */
  localesEnabled: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// MeResponse is the wire-format shape returned by /v1/portal/auth/me.
// Canonical Zod-schema lives in @orboto/ocp-shared so the api response
// and the UI's TS-types are guaranteed identical.
type MeResponse = PortalUserPayload;

function fromMeResponse(m: MeResponse): PortalUser {
  return {
    id: m.id,
    customerId: m.customer_id,
    email: m.email,
    emailVerified: m.email_verified,
    role: m.role,
    totpEnabled: m.totp_enabled,
    pendingRedeemCode: m.pending_redeem_code,
    locale: isPortalLocale(m.locale) ? m.locale : null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<PortalUser | null>(null);

  const fetchMe = useCallback(async (signal?: AbortSignal) => {
    try {
      const m = await api<MeResponse>('/v1/portal/auth/me', { signal });
      const u = fromMeResponse(m);
      setUser(u);
      setStatus('authenticated');
      // OCP-656 - the stored choice wins over the browser default.
      if (u.locale && u.locale !== currentLocale() && getPortalConfig().localesEnabled) void applyLocale(u.locale);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void fetchMe(ac.signal);
    return () => ac.abort();
  }, [fetchMe]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      // The login endpoint can return either an authenticated payload
      // (no TOTP) or a TOTP-challenge (HTTP 200 + `status: totp_required`).
      // Wrong-credentials is HTTP 401 — bubbled to caller as ApiError.
      const r = await api<
        | { status: 'authenticated'; user: MeResponse }
        | { status: 'totp_required'; login_token: string }
      >('/v1/portal/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      if (r.status === 'totp_required') {
        return { status: 'totp_required', loginToken: r.login_token };
      }
      setUser(fromMeResponse(r.user));
      setStatus('authenticated');
      return { status: 'authenticated' };
    },
    [],
  );

  const loginTotp = useCallback(async (loginToken: string, code: string) => {
    const r = await api<{ user: MeResponse }>('/v1/portal/auth/login/totp', {
      method: 'POST',
      body: { login_token: loginToken, code },
    });
    setUser(fromMeResponse(r.user));
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/v1/portal/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort. Local state still resets so the UI bounces to /login.
    }
    setUser(null);
    setStatus('unauthenticated');
    // OCP-212: drop the cached OMS-subscription answer so a different
    // customer logging in on the same browser session sees a fresh
    // fetch instead of inheriting the previous one's gate state.
    resetOmsSubscriptionCache();
    // OCP-490: same rationale for the affiliate nav/dashboard gate.
    resetAffiliateCache();
  }, []);

  const refresh = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const { localesEnabled } = usePortalConfig();
  useEffect(() => {
    // OCP-656 - the switch may be released after /me answered: apply the stored choice then.
    if (localesEnabled && user?.locale && user.locale !== currentLocale()) void applyLocale(user.locale);
  }, [localesEnabled, user?.locale]);

  const setLocale = useCallback(
    async (locale: PortalLocale) => {
      await applyLocale(locale);
      if (status !== 'authenticated') return;
      await api('/v1/portal/me/locale', { method: 'PATCH', body: { locale } });
      setUser((prev) => (prev ? { ...prev, locale } : prev));
    },
    [status],
  );

  return (
    <AuthContext.Provider value={{ status, user, login, loginTotp, logout, refresh, setLocale, localesEnabled }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/**
 * Same as `useAuth`, but returns null instead of throwing when there is
 * no provider above (OCP-545). For cross-cutting helpers that only WANT
 * the session - a role-aware UI hint, say - and must not turn a missing
 * provider into a render crash. Anything that NEEDS the session keeps
 * using `useAuth`.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext) ?? null;
}

export { ApiError };
